// Interpolation between two artworkPalette objects, for the cover crossfade's
// scene half (see scene-manager.js's beginPaletteBlend).

// Slightly longer than artwork-transition.js's own 700ms, on purpose: the
// scene finishes turning just AFTER the picture has landed, which reads as the
// room following the cover rather than as two things going off at once.
export const PALETTE_BLEND_MS = 900;

// How many distinct palette objects get published across that window. Not a
// per-frame interpolation - see the long note above beginPaletteBlend for why
// that would be a per-frame attribute upload across fourteen visualizers.
// Six is enough that the eye reads a sweep rather than a step, and few enough
// that the expensive consumers rebuild six times in 900ms.
export const PALETTE_BLEND_STEPS = 6;

// sRGB channel mix. Deliberately NOT a perceptual space: these values feed
// THREE.Color.set() and CSS alike, both of which take plain sRGB, and a
// round trip through OKLab per channel per step would cost more than the
// difference is worth on a palette that is halfway through a 900ms fade.
function lerpHex(a, b, t) {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

// Colour lists can differ in length between two covers. Blending index-wise
// only as far as both go, then taking the target's remainder outright, keeps
// every consumer's `vivid[i % vivid.length]` indexing valid at every step -
// a list that changed length mid-blend would reshuffle which particle holds
// which colour, and that reads as a glitch, not a fade.
function lerpList(from, to, t) {
  if (!Array.isArray(from) || from.length === 0) return to;
  if (!Array.isArray(to) || to.length === 0) return to;
  const out = [];
  for (let i = 0; i < to.length; i++) {
    out.push(i < from.length ? lerpHex(from[i], to[i], t) : to[i]);
  }
  return out;
}

function lerpNumber(from, to, t) {
  if (typeof from !== 'number' || typeof to !== 'number') return to;
  return from + (to - from) * t;
}

// Returns a NEW object every call - the identity change is exactly what the
// visualizers' `!== this._lastPaletteRef` guards are watching for.
export function lerpPalette(from, to, t) {
  if (!from) return to;
  if (!to) return from;
  return {
    dominantColors: lerpList(from.dominantColors, to.dominantColors, t),
    vividColors: lerpList(from.vividColors, to.vividColors, t),
    brightness: lerpNumber(from.brightness, to.brightness, t),
    saturation: lerpNumber(from.saturation, to.saturation, t),
    warmth: lerpNumber(from.warmth, to.warmth, t)
  };
}
